import * as _ from "lodash";
import * as angular from "angular";
import { PermissionCheck } from "../../shared/permission";
import { UserPermission } from "../../utils/user-permissions";
import { InviteService } from "../../services/invite.service";
import { UserDataProvider } from "../../data-provider/user.dp";
import { DialogUtils } from "../../helper/dialog.helper";
import { ToastUtils } from "../../helper/toast.helper";
import { Team } from "../../models/team.model";
import { Campaign } from "../../models/campaign.model";
import { CampaignDataProvider } from "../../data-provider/campaign.dp";
import { TeamDataProvider } from "../../data-provider/team.dp";
import { QrCodeService } from "../../services/qr-code.service";
import {
    copyImageToClipboard,
    copyToClipboard,
} from "../../utils/clipboard.util";
import { downloadDataUri } from "../../utils/download.util";

let UserInviteComponent = {
    selector: "vcUserInvite",
    templateUrl: "/angular/components/invite/user-invite.component.html",
    bindings: {},
    controller: class UserInviteController {
        // * bindings for typescript compiler *
        public inviteUser?: any;
        // ************************************

        private $scope: any;
        private service: InviteService;
        private toast: ToastUtils;
        private dialog: DialogUtils;
        private modelsUtils: any;
        private permissionProvider: PermissionCheck;
        private userDataProvider: UserDataProvider;
        private readonly __env: any;

        public readonly UserPermission = {
            LEAD_ORGANIZER: UserPermission.LEAD_ORGANIZER,
            ORGANIZER: UserPermission.ORGANIZER,
        };

        // Stable array reference — returned from allowedPermissionIds() so the
        // one-way `allowed-permissions` binding doesn't see a new array every
        // digest (which triggers an infinite digest and breaks the radio).
        private readonly PHONE_PERMISSIONS = [
            UserPermission.LEAD_ORGANIZER,
            UserPermission.ORGANIZER,
        ];

        // is the input for the chosen method (email / phone / QR).
        public step = 0;

        public activeTab = "email";
        public activeCampaign: Campaign;
        public errorMessage = "";
        public products: Array<any>;
        public emails: string[] = [];
        public error = "";
        public usersCount: object;
        public user;
        public invite_loading: boolean;
        // Step 0 is the "How would you like to invite?" method picker; step 1
        public qrEnabled: boolean = false;
        public editingStep = false;

        public qrOptionVisible: boolean = false;
        public qrSettingsLoaded: boolean = false;
        public qrRequireApproval: boolean = false;
        public qrImage: string = "";
        public qrUrl: string = "";
        public qrTeams: Team[] = [];
        public qrTeamId: number | null = null;
        // ("link" | "image" | ""), and the timer that clears it.
        public copiedKind: string = "";
        // Which share row is showing its inline "copied" confirmation
        // Only owners/managers may turn campaign QR sign-up on or off.
        private readonly QR_MANAGE_PERMISSIONS: number[] = [
            UserPermission.ACCOUNT_OWNER, // 2
            UserPermission.ACCOUNT_MANAGER, // 3
            UserPermission.LEADER_OWNER, // 8
            UserPermission.LEADER_MANAGER, // 9
            UserPermission.DIVISION_MANAGER, // 10
        ];
        private copiedTimer: any;
        private readonly $timeout: any;
        private qrSettings: {
            enabled: boolean;
            team_id: number | null;
            require_manager_approval: boolean;
        } | null = null;
        private qrLoaded: boolean = false;
        private readonly qrCodeService: QrCodeService;
        private readonly teamProvider: TeamDataProvider;

        public steps = [
            { name: "contact", position: 1 },
            { name: "permission", position: 2 },
            { name: "app_type", position: 3 },
            { name: "teams", position: 4 },
            { name: "confirmation", position: 5 },
        ];

        constructor(
            $scope: any,
            $timeout: any,
            InviteService: InviteService,
            ToastUtils: ToastUtils,
            DialogUtils: DialogUtils,
            ModelsUtils: any,
            PermissionCheck: PermissionCheck,
            UserDataProvider: UserDataProvider,
            CampaignDataProvider: CampaignDataProvider,
            __env: any,
            QrCodeService: QrCodeService,
            TeamDataProvider: TeamDataProvider
        ) {
            this.$scope = $scope;
            this.$timeout = $timeout;
            this.service = InviteService;
            this.toast = ToastUtils;
            this.dialog = DialogUtils;
            this.modelsUtils = ModelsUtils;
            this.permissionProvider = PermissionCheck;
            this.userDataProvider = UserDataProvider;
            this.__env = __env;
            this.qrCodeService = QrCodeService;
            this.teamProvider = TeamDataProvider;
            this.products = [this.__env.product1, this.__env.product2];

            this.activeCampaign = CampaignDataProvider.getActiveCampaign();

            this.user = ModelsUtils.user({
                teams: [],
                app_type: "Walk",
            });

            this.userDataProvider
                .getUsersCount()
                .then((count: object) => (this.usersCount = count));
        }

        $onInit(): void {
            // Seed from the synchronous permission checks so the QR card is
            // on the method picker from the first paint for anyone who can
            // manage QR, instead of popping in when the settings request
            // below resolves. Only viewers who can't manage QR but whose
            // campaign has it enabled need the async `enabled` flag.
            this.qrOptionVisible =
                this.permissionProvider.featureGrants("QR_SIGNUP_DISPLAY") &&
                this.permissionProvider.featureGrants("QR_SIGNUP") &&
                this.canEnableQr();
            if (this.qrOptionVisible) {
                this.qrCodeService.getSettings().then(
                    (res: any) => {
                        const s = res.data.data;
                        this.qrSettings = {
                            enabled: !!s.enabled,
                            team_id: s.team_id ?? null,
                            require_manager_approval:
                                !!s.require_manager_approval,
                        };
                        this.qrTeamId = this.qrSettings.team_id;
                        this.qrRequireApproval =
                            this.qrSettings.require_manager_approval;
                        this.qrEnabled = this.qrSettings.enabled;
                        this.qrSettingsLoaded = true;
                        this.teamProvider
                            .getAllTeams()
                            .then((teams: Team[]) => {
                                // Coerce ids to numbers so the md-select
                                // preselects correctly against the int team_id.
                                this.qrTeams = teams.map((t: any) => ({
                                    ...t,
                                    tid: Number(t.tid),
                                }));
                            });
                        // Preload the code image while the user is still on the
                        // method picker so the QR panel renders it immediately on
                        // click (avoids a reflow mid-transition when it pops in).
                        if (this.qrEnabled) {
                            this.loadQrCode();
                        }
                    },
                    () => this.toast.error("Failed to load QR settings")
                );
            }
        }

        // Whether to show a link to the QR Sign-up settings page: the user must
        // be able to open Customise and the QR tab must be available to them.
        canManageQrSettings(): boolean {
            return (
                this.permissionProvider.hasPermission("CAMPAIGN_SETTINGS") &&
                this.permissionProvider.featureGrants("QR_SIGNUP")
            );
        }

        // Persists the campaign's QR sign-up settings when changed from the QR
        // screen (mirrors the controls from the old QR settings tab).
        saveQrTeam(): void {
            this.persistQrSettings();
        }

        saveQrApproval(): void {
            this.persistQrSettings();
        }

        // permission step entirely, so it has no entry here.
        allowedPermissionIds(): number[] | null {
            if (this.activeTab == "phone") {
                return this.PHONE_PERMISSIONS;
            }
            return null;
        }

        editStep(step: number) {
            this.step = step;
            this.editingStep = true;
        }

        finishStep(stage: string): void {
            if (this.step == 1) {
                // Keep only the recipients for the chosen method.
                if (this.activeTab == "email") {
                    this.user.phones = [];
                    this.user.uphonenumber = "";
                } else if (this.activeTab == "phone") {
                    this.emails = [];
                }
            }
            if (this.editingStep) {
                this.step = 5;
                this.editingStep = false;
                return;
            }

            this.editingStep = false;

            angular.forEach(
                this.steps,
                (currentStep: { name: string; position: number }) => {
                    if (currentStep.name == stage) {
                        this.step += 1;
                        if (
                            ([3, 4].includes(this.user.permissionid) ||
                                !this.permissionProvider.hasPermission(
                                    "GO_APP"
                                )) &&
                            this.step == 3
                        ) {
                            this.step = 4;
                        }
                    }
                }
            );
        }

        stepBack(): void {
            this.editingStep = false;
            this.step -= 1;
            if (
                ([3, 4].includes(this.user.permissionid) ||
                    !this.permissionProvider.hasPermission("GO_APP")) &&
                this.step == 3
            ) {
                this.step = 2;
            }
        }

        // Which permission levels the permission step offers, based on the
        // chosen contact method. null = no restriction (all send-allowed
        // levels). Phone allows lead organiser + organiser. QR skips the

        // to its input step.
        selectMethod(tab: string): void {
            this.setActiveTab(tab);
            this.step = 1;
        }

        // From the method picker (step 0): choose a contact method and advance

        // Whether the current user may turn QR sign-up on or off.
        canEnableQr(): boolean {
            return this.QR_MANAGE_PERMISSIONS.includes(
                Number(this.permissionProvider.permissionid)
            );
        }

        enableQr(): void {
            this.setQrEnabled(true);
        }

        // Disabling stops the shared code working, so confirm first.
        disableQr(): void {
            this.dialog
                .show({
                    component: "vc-general-info",
                    multiple: true,
                    fullscreen: false,
                    clickOutsideToClose: true,
                    locals: {
                        title: "Disable QR sign-up?",
                        bodyText:
                            "The QR code and sign-up link will stop working, and people will no longer be able to request to join by scanning it. You can turn it back on at any time.",
                        buttonText: { confirm: "Disable", cancel: "Cancel" },
                    },
                })
                .then(
                    () => this.setQrEnabled(false),
                    () => {
                        /* cancelled */
                    }
                );
        }

        setActiveTab(tab: string): void {
            this.activeTab = tab;
            // Only fetch the shared code once QR sign-up is actually enabled;
            // when disabled the tab shows the explainer instead.
            if (tab == "qr" && this.qrEnabled) {
                this.loadQrCode();
            }
            // Drop a previously-chosen permission that the new method disallows
            // so the user must pick an allowed one.
            const allowed = this.allowedPermissionIds();
            if (
                allowed &&
                this.user.permissionid &&
                !allowed.includes(this.user.permissionid)
            ) {
                this.user.permissionid = null;
                this.user.permission = {};
            }
        }

        copyQrLink(): void {
            copyToClipboard(this.qrUrl);
            this.flashCopied("link");
        }

        copyQrImage(): void {
            copyImageToClipboard(this.qrImage).then(
                () => this.flashCopied("image"),
                () => this.toast.error("Couldn't copy image to clipboard")
            );
        }

        loadQrCode(): void {
            if (this.qrLoaded) {
                return;
            }
            this.qrCodeService.campaignCode().then(
                (res: any) => this.applyCode(res.data.data),
                () => this.toast.error("Failed to load QR code")
            );
        }

        private applyCode(code: { image: string; url: string }): void {
            this.qrImage = code.image;
            this.qrUrl = code.url;
            this.qrLoaded = true;
        }

        // stops working.
        regenerateQr(): void {
            this.dialog
                .show({
                    component: "vc-general-info",
                    multiple: true,
                    fullscreen: false,
                    clickOutsideToClose: true,
                    locals: {
                        title: "Regenerate QR code?",
                        bodyText:
                            "This creates a new code and link. The current QR code and link will stop working immediately - anyone who already has the old one won't be able to use it.",
                        buttonText: {
                            confirm: "Regenerate",
                            cancel: "Cancel",
                        },
                    },
                })
                .then(
                    () => this.doRegenerateQr(),
                    () => {
                        /* cancelled — nothing to do */
                    }
                );
        }

        private persistQrSettings(): void {
            const settings = this.qrSettings;
            if (!settings) {
                return;
            }
            this.qrCodeService
                .saveSettings({
                    enabled: settings.enabled,
                    team_id: this.qrTeamId,
                    require_manager_approval: this.qrRequireApproval,
                })
                .then(
                    () => {
                        settings.team_id = this.qrTeamId;
                        settings.require_manager_approval =
                            this.qrRequireApproval;
                    },
                    () => this.toast.error()
                );
        }

        // Show an inline "copied" confirmation on the given share row that

        private setQrEnabled(enabled: boolean): void {
            const settings = this.qrSettings;
            if (!settings) {
                return;
            }
            const previous = settings.enabled;
            // Flip the screen immediately so it doesn't sit on the old view
            // during the save round-trip; persist in the background and revert
            // if it fails.
            settings.enabled = enabled;
            this.qrEnabled = enabled;
            if (enabled) {
                this.loadQrCode();
            }
            this.qrCodeService
                .saveSettings({
                    enabled: enabled,
                    team_id: this.qrTeamId,
                    require_manager_approval: this.qrRequireApproval,
                })
                .then(
                    () => {
                        /* saved */
                    },
                    () => {
                        settings.enabled = previous;
                        this.qrEnabled = previous;
                        this.toast.error();
                    }
                );
        }

        downloadQr(): void {
            downloadDataUri(this.qrImage, "qr-code.png");
        }

        // Rotate the campaign's QR token, invalidating the current code/link.
        // Confirms first because it can't be undone — any already-shared code

        // fades away after a couple of seconds (replaces the toast).
        private flashCopied(kind: string): void {
            this.copiedKind = kind;
            if (this.copiedTimer) {
                this.$timeout.cancel(this.copiedTimer);
            }
            this.copiedTimer = this.$timeout(() => {
                this.copiedKind = "";
            }, 2000);
            // copyQrImage resolves a native Promise outside Angular's digest,
            // so schedule one to render the change (safe if already digesting).
            this.$scope.$applyAsync();
        }

        private doRegenerateQr(): void {
            this.qrCodeService.regenerateCampaignCode().then(
                (res: any) => {
                    this.applyCode(res.data.data);
                    this.toast.success("QR code regenerated");
                },
                () => this.toast.error("Couldn't regenerate the QR code")
            );
        }

        // Recipients on the confirmation screen: emails, or the phone numbers.
        recipientCount(): number {
            if (this.activeTab == "phone") {
                return this.user.phones ? this.user.phones.length : 0;
            }
            return this.emails.length;
        }

        sendInvite(): void {
            this.invite_loading = true;
            this.errorMessage = "";

            let data = _.cloneDeep(this.user);
            data.inviter_permission = this.permissionProvider.permissionid;
            data.apptype = this.products.indexOf(this.user.app_type);
            if (data.apptype < 0) data.apptype = 0;

            if (data.teams && data.teams.length) {
                let tids = [];
                angular.forEach(data.teams, (team: Team) => {
                    tids.push(team.tid);
                });
                data.teams = tids;
            }

            if (this.activeTab == "phone") {
                this.sendPhoneInvites(data);
                return;
            }

            data.uphonenumber = null;
            data.ucountrycode = null;
            data.emails = this.emails;

            this.service.create(data).then((response: object) => {
                const json = response["data"];
                this.invite_loading = false;
                if (json.success) {
                    this.userDataProvider.resetUsersCount();
                    if (json.added) {
                        this.toast.success("User Added");
                        this.dialog.hide();
                    } else {
                        if (this.activeTab == "email") {
                            this.toast.success("Invites sending...");
                        } else {
                            this.toast.success("User Invited");
                        }
                        this.dialog.hide();
                    }
                } else {
                    if (json.message != null) {
                        this.errorMessage = json.message;
                    } else if (json.data.message != null) {
                        this.errorMessage = json.data.message;
                    }
                }
            });
        }

        // The invite API takes one phone number per request, so send them one
        // at a time and, once all are done, report how many were sent and which
        // numbers failed. A partial or total failure keeps the dialog open so
        // the user can see what went wrong and retry.
        private sendPhoneInvites(base: any): void {
            const phones = (this.user.phones || []).slice();
            base.uemail = null;
            base.emails = [];

            let firstError = "";
            let successCount = 0;
            const failedNumbers: string[] = [];

            const sendNext = (index: number): void => {
                if (index >= phones.length) {
                    this.invite_loading = false;
                    if (successCount > 0) {
                        this.userDataProvider.resetUsersCount();
                    }
                    if (failedNumbers.length === 0) {
                        this.toast.success(
                            phones.length > 1 ? "Users Invited" : "User Invited"
                        );
                        this.dialog.hide();
                        return;
                    }
                    if (successCount === 0) {
                        this.errorMessage =
                            firstError || "None of the invites could be sent.";
                        return;
                    }
                    this.errorMessage =
                        "Invited " +
                        successCount +
                        " of " +
                        phones.length +
                        ". Couldn't invite: " +
                        failedNumbers.join(", ") +
                        ".";
                    return;
                }

                const data = _.cloneDeep(base);
                data.uphonenumber = phones[index].number;
                data.ucountrycode = phones[index].countrycode;

                const recordFailure = (message?: string): void => {
                    failedNumbers.push(phones[index].number);
                    if (!firstError && message) {
                        firstError = message;
                    }
                };

                this.service.create(data).then(
                    (response: object) => {
                        const json = response["data"];
                        if (json.success) {
                            successCount += 1;
                        } else {
                            recordFailure(json.message ?? json.data?.message);
                        }
                        sendNext(index + 1);
                    },
                    () => {
                        recordFailure("Some invites couldn't be sent.");
                        sendNext(index + 1);
                    }
                );
            };

            sendNext(0);
        }

        cancel(): void {
            this.dialog.cancel();
        }
    },
};

angular
    .module("invite")
    .component(UserInviteComponent.selector, UserInviteComponent);
